import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getBotResponse, INITIAL_GREETING, ChatMessage, Language } from '../utils/ChatbotData';
import { getGeminiResponse } from '../utils/geminiService';
import { useData } from '../context/DataContext';

interface ChatWindowProps {
    isOpen: boolean;
    onClose: () => void;
    phoneNumber: string;
    whatsappMessage: string;
}

const ChatWindow: React.FC<ChatWindowProps> = ({ isOpen, onClose, phoneNumber, whatsappMessage }) => {
    const { settings, trucks, posts } = useData();
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [inputText, setInputText] = useState('');
    const [currentLanguage, setCurrentLanguage] = useState<Language>('en');
    const [isTyping, setIsTyping] = useState(false);
    const [interactionCount, setInteractionCount] = useState(0);
    const [conversationHistory, setConversationHistory] = useState<Array<{ role: 'user' | 'model', parts: string }>>([]);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Chatbot Configuration
    const { chatbotConfig, logoUrl: siteLogo } = settings;
    const botLogo = chatbotConfig?.logoUrl || siteLogo;
    const greeting = chatbotConfig?.welcomeMessage || INITIAL_GREETING;
    const placeholder = chatbotConfig?.placeholderText || "Type a message...";
    const primaryColor = chatbotConfig?.primaryColor || "#25D366";
    const displayName = chatbotConfig?.displayName || "Automax Support";

    // Simple markdown renderer for bot messages
    const renderMessage = useMemo(() => (text: string, isBotMessage: boolean) => {
        if (!isBotMessage) return text;

        // Split into lines and process
        const lines = text.split('\n');
        const elements: React.ReactNode[] = [];

        lines.forEach((line, lineIndex) => {
            // Process inline markdown within a line
            const processInline = (str: string): React.ReactNode[] => {
                const parts: React.ReactNode[] = [];
                // Match bold (**text**), links [text](url), and plain text
                const regex = /(\*\*(.+?)\*\*)|\[([^\]]+)\]\(([^)]+)\)/g;
                let lastIndex = 0;
                let match;

                while ((match = regex.exec(str)) !== null) {
                    // Add text before match
                    if (match.index > lastIndex) {
                        parts.push(str.slice(lastIndex, match.index));
                    }

                    if (match[1]) {
                        // Bold **text**
                        parts.push(<strong key={`b-${lineIndex}-${match.index}`} className="font-semibold">{match[2]}</strong>);
                    } else if (match[3] && match[4]) {
                        // Link [text](url)
                        const url = match[4];
                        const siteUrl = settings?.siteUrl || 'https://automaxspace.com';

                        // Check if this is an internal site link
                        const isInternalSite = url.startsWith(siteUrl) || url.startsWith('/');
                        const isWhatsApp = url.includes('wa.me') || url.includes('whatsapp');

                        if (isInternalSite) {
                            // Internal link — navigate within the SPA
                            const path = url.startsWith('/') ? url : new URL(url).pathname;
                            parts.push(
                                <a
                                    key={`a-${lineIndex}-${match.index}`}
                                    href={path}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        window.location.href = path;
                                    }}
                                    className="text-blue-600 underline hover:text-blue-800 cursor-pointer"
                                >
                                    {match[3]}
                                </a>
                            );
                        } else {
                            // External link — open in new tab
                            parts.push(
                                <a
                                    key={`a-${lineIndex}-${match.index}`}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 underline hover:text-blue-800"
                                >
                                    {match[3]}
                                </a>
                            );
                        }
                    }
                    lastIndex = match.index + match[0].length;
                }

                // Add remaining text
                if (lastIndex < str.length) {
                    parts.push(str.slice(lastIndex));
                }

                return parts.length > 0 ? parts : [str];
            };

            const trimmedLine = line.trim();

            // Bullet points (• or - at start of line)
            if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-')) {
                const bulletContent = trimmedLine.replace(/^[•\-]\s*/, '');
                elements.push(
                    <div key={`l-${lineIndex}`} className="flex items-start gap-1.5 ml-1">
                        <span className="mt-0.5 text-xs">•</span>
                        <span>{processInline(bulletContent)}</span>
                    </div>
                );
            } else if (trimmedLine === '') {
                // Empty line = paragraph break
                elements.push(<div key={`l-${lineIndex}`} className="h-2" />);
            } else {
                // Normal text line
                elements.push(
                    <span key={`l-${lineIndex}`}>
                        {lineIndex > 0 && elements.length > 0 && !lines[lineIndex - 1]?.trim().startsWith('•') && !lines[lineIndex - 1]?.trim().startsWith('-') && lines[lineIndex - 1]?.trim() !== '' && <br />}
                        {processInline(trimmedLine)}
                    </span>
                );
            }
        });

        return <>{elements}</>;
    }, [settings]);

    // Initial Greeting
    useEffect(() => {
        if (isOpen && messages.length === 0) {
            setIsTyping(true);
            setTimeout(() => {
                setMessages([
                    {
                        id: '1',
                        text: greeting,
                        sender: 'bot',
                        timestamp: new Date()
                    }
                ]);
                setIsTyping(false);
            }, 600);
        }
    }, [isOpen, greeting]);

    // Auto-scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isTyping]);

    const handleSendMessage = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!inputText.trim()) return;

        const userMsg: ChatMessage = {
            id: Date.now().toString(),
            text: inputText,
            sender: 'user',
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMsg]);
        setInputText('');
        setIsTyping(true);

        // Update interaction count
        const currentCount = interactionCount;
        setInteractionCount(prev => prev + 1);

        try {
            // Try Gemini AI first with a timeout to prevent hanging
            const TIMEOUT_MS = 25000;
            const timeoutPromise = new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error(`Request timed out after ${TIMEOUT_MS}ms`)), TIMEOUT_MS)
            );

            const conversationPromise = getGeminiResponse(
                userMsg.text,
                conversationHistory,
                trucks,
                posts,
                settings.chatbotAIConfig,
                settings
            );

            // Race the API call against the timeout
            const geminiResult = await Promise.race([
                conversationPromise,
                timeoutPromise
            ]) as Awaited<ReturnType<typeof getGeminiResponse>>; // Ensure type safety

            if (geminiResult.error || !geminiResult.text || !geminiResult.text.trim()) {
                // Fallback to rule-based system on error or empty response
                console.warn('Gemini issue, falling back to rule-based:', geminiResult.error || 'Empty response');
                const { text, shouldHandover, detectedLanguage } = getBotResponse(
                    userMsg.text,
                    currentCount,
                    currentLanguage,
                    trucks,
                    posts
                );

                if (detectedLanguage && detectedLanguage !== currentLanguage) {
                    setCurrentLanguage(detectedLanguage);
                }

                const botMsg: ChatMessage = {
                    id: (Date.now() + 1).toString(),
                    text: text,
                    sender: 'bot',
                    timestamp: new Date()
                };

                setMessages(prev => [...prev, botMsg]);
                setIsTyping(false);

                if (shouldHandover) {
                    setTimeout(() => {
                        setMessages(prev => [...prev, {
                            id: (Date.now() + 2).toString(),
                            text: "Click below to connect with us on WhatsApp:",
                            sender: 'bot',
                            timestamp: new Date(),
                            isAction: true,
                            actionType: 'whatsapp_link'
                        }]);
                    }, 500);
                }
            } else {
                // Use Gemini response
                const botMsg: ChatMessage = {
                    id: (Date.now() + 1).toString(),
                    text: geminiResult.text,
                    sender: 'bot',
                    timestamp: new Date()
                };

                setMessages(prev => [...prev, botMsg]);
                setIsTyping(false);

                // Update conversation history
                setConversationHistory(prev => [
                    ...prev,
                    { role: 'user', parts: userMsg.text },
                    { role: 'model', parts: geminiResult.text }
                ]);

                if (geminiResult.shouldHandover) {
                    setTimeout(() => {
                        setMessages(prev => [...prev, {
                            id: (Date.now() + 2).toString(),
                            text: "Click below to connect with us on WhatsApp:",
                            sender: 'bot',
                            timestamp: new Date(),
                            isAction: true,
                            actionType: 'whatsapp_link'
                        }]);
                    }, 500);
                }
            }
        } catch (error) {
            console.error('Error in chatbot:', error);
            // Final fallback
            const botMsg: ChatMessage = {
                id: (Date.now() + 1).toString(),
                text: "I'm having trouble processing your request. Let me connect you with our team on WhatsApp for immediate assistance.",
                sender: 'bot',
                timestamp: new Date()
            };
            setMessages(prev => [...prev, botMsg]);
            setIsTyping(false);

            setTimeout(() => {
                setMessages(prev => [...prev, {
                    id: (Date.now() + 2).toString(),
                    text: "Click below to connect with us on WhatsApp:",
                    sender: 'bot',
                    timestamp: new Date(),
                    isAction: true,
                    actionType: 'whatsapp_link'
                }]);
            }, 500);
        }
    };

    const handleWhatsAppRedirect = () => {
        const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(whatsappMessage)}`;
        window.open(url, '_blank');
    };

    // if (!isOpen) return null; // Removed to preserve state

    return (
        <>
            {/* Styles removed as we use Tailwind transitions now */}
            <div
                className={`fixed bottom-24 right-6 w-80 md:w-96 h-[500px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden z-50 border border-gray-100 origin-bottom-right transition-all duration-300 ease-out ${isOpen
                    ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto'
                    : 'opacity-0 translate-y-5 scale-95 pointer-events-none'
                    }`}
            >
                {/* Header */}
                <div
                    className="p-4 flex items-center justify-between text-white transition-colors duration-300"
                    style={{ backgroundColor: primaryColor }}
                >
                    <div className="flex items-center space-x-3">
                        <div className="relative">
                            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm overflow-hidden">
                                <img
                                    src={botLogo}
                                    alt="Bot Logo"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div
                                className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 border-2 rounded-full"
                                style={{ borderColor: primaryColor }}
                            ></div>
                        </div>
                        <div>
                            <h3 className="font-bold text-lg leading-tight">{displayName}</h3>
                            <p className="text-xs text-white/80">Online | Replies Instantly</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-lg transition-colors">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                </div>

                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#ece5dd] scrollbar-thin scrollbar-thumb-gray-300">
                    {messages.map((msg) => (
                        <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div
                                className={`max-w-[80%] p-3 rounded-2xl text-sm shadow-sm ${msg.sender === 'user'
                                    ? 'bg-[#d9fdd3] text-gray-800 rounded-tr-none'
                                    : 'bg-white text-gray-800 rounded-tl-none'
                                    }`}
                            >
                                {msg.sender === 'bot' ? renderMessage(msg.text, true) : msg.text}
                                {msg.isAction && msg.actionType === 'whatsapp_link' && (
                                    <button
                                        onClick={handleWhatsAppRedirect}
                                        className="mt-3 w-full text-white py-2 px-4 rounded-lg flex items-center justify-center space-x-2 transition-all duration-200 font-medium hover:opacity-90"
                                        style={{ backgroundColor: primaryColor }}
                                    >
                                        <span>Continue on WhatsApp</span>
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12.0117 2C6.50574 2 2.02344 6.47837 2.02344 11.9841C2.02344 13.7485 2.48166 15.463 3.37682 16.9926L2 22L7.15282 20.6508C8.61808 21.4485 10.2831 21.8683 11.9866 21.8683C17.4925 21.8683 21.9749 17.39 21.9749 11.8841C21.9749 6.37837 17.4939 2 12.0117 2Z"></path></svg>
                                    </button>
                                )}
                                <div className="text-[10px] text-gray-400 mt-1 text-right">
                                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                            </div>
                        </div>
                    ))}

                    {isTyping && (
                        <div className="flex justify-start">
                            <div className="bg-white p-3 rounded-2xl rounded-tl-none shadow-sm flex space-x-1 items-center">
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <form onSubmit={handleSendMessage} className="p-3 bg-gray-50 border-t border-gray-100 flex items-center space-x-2">
                    <input
                        type="text"
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        placeholder={placeholder}
                        className="flex-1 px-4 py-2 border border-gray-200 rounded-full focus:outline-none focus:ring-1 text-sm bg-white text-gray-900"
                        style={{
                            caretColor: primaryColor,
                        }}
                    />
                    <button
                        type="submit"
                        disabled={!inputText.trim() || isTyping}
                        className="p-2 rounded-full text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
                        style={{ backgroundColor: primaryColor }}
                    >
                        <svg className="w-5 h-5 translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path></svg>
                    </button>
                </form>
            </div>
        </>
    );
};

export default ChatWindow;
